import { useState, useCallback, Fragment, useMemo } from 'react';
import { Text } from '@hotjar/hotsauce';
import type { AbstractSingleFilterDropdownProps } from '../../SingleFilterType';
import { GenericFilterBody } from '../GenericFilter/GenericFilterBody';
import { GenericFilterDropdown } from '../GenericFilter/GenericFilterDropdown';
import { GenericFilterFooter } from '../GenericFilter/GenericFilterFooter';
import { FilterItemRadio, FilterRadioContainer } from '../GenericSubElement/FilterItemRadio';
import type { RadioBooleanFilterValueType } from './GenericRadioBooleanType';
import { RadioBooleanFilterOperation } from './GenericRadioBooleanType';

enum ValidationErrorEnum {
  SELECT_ONE_OPTION = 'Select an option',
}

export const readableRadioBooleanOperations: Record<RadioBooleanFilterOperation, string> = {
  [RadioBooleanFilterOperation.IS_TRUE]: 'is "true"',
  [RadioBooleanFilterOperation.IS_FALSE]: 'is "false"',
  [RadioBooleanFilterOperation.IS_UNKNOWN]: 'is unknown',
  [RadioBooleanFilterOperation.HAS_ANY_VALUE]: 'has any value',
};

interface GenericRadioDropdownProps<T> extends AbstractSingleFilterDropdownProps<T> {
  options: RadioBooleanFilterOperation[];
}

function getFilterValueErrors(
  operation?: RadioBooleanFilterOperation,
): ValidationErrorEnum | undefined {
  if (!operation) {
    return ValidationErrorEnum.SELECT_ONE_OPTION;
  }
  return undefined;
}

export function GenericRadioBooleanDropdown({
  currentValue,
  optionalDefaultValue,
  onApply,
  options = [],
  filterName,
}: GenericRadioDropdownProps<RadioBooleanFilterValueType>): JSX.Element {
  const initialValue = useMemo(
    () => currentValue ?? optionalDefaultValue,
    [currentValue, optionalDefaultValue],
  );
  const [dirtyOperation, setDirtyOperation] = useState<RadioBooleanFilterOperation | undefined>(
    initialValue?.operation ?? options.find(() => true),
  );

  const [validationError, setValidationError] = useState<ValidationErrorEnum>();
  const onOperationChange = useCallback(
    (newOperation: RadioBooleanFilterOperation) => {
      setDirtyOperation(newOperation);
      setValidationError(undefined);
    },
    [setDirtyOperation, setValidationError],
  );

  const handleApply = useCallback(() => {
    const filterValueErrors = getFilterValueErrors(dirtyOperation);
    if (filterValueErrors) {
      setValidationError(filterValueErrors);
    } else if (dirtyOperation) {
      onApply({
        operation: dirtyOperation,
      });
    }
  }, [onApply, dirtyOperation]);

  return (
    <GenericFilterDropdown ariaLabel={`${filterName} dropdown`} onApply={handleApply}>
      <GenericFilterBody>
        <FilterRadioContainer>
          {options.map((option) => {
            return (
              <Fragment key={option}>
                <FilterItemRadio
                  isChecked={option === dirtyOperation}
                  id={`${filterName}-dropdown-${option}`}
                  name={option}
                  onChange={() => onOperationChange(option)}
                  value={`${option}`}
                  labelContent={readableRadioBooleanOperations[option]}
                  isInvalid={validationError === ValidationErrorEnum.SELECT_ONE_OPTION}
                />
              </Fragment>
            );
          })}
          {validationError === ValidationErrorEnum.SELECT_ONE_OPTION && (
            <Text textColor="text_error">{validationError}</Text>
          )}
        </FilterRadioContainer>
      </GenericFilterBody>
      <GenericFilterFooter />
    </GenericFilterDropdown>
  );
}
